/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import { COMMUNICATION_TYPE } from '@/Core/config/communicationType';
import { feathers } from '@/feathers-client';
import { ESSearchHeader } from '@/Core/config/es-search-header';
import {
  ES_STATUS
} from '@/Contacts/config/profile-optin-status'

/**
 * Returns the parsed contact object depending on the type
 * @param {Object} payload
 * @param {Object} payload.rawContactObject The unparsed contact object
 * @param {Object} payload.type The desired type it should be parsed upon
 * @param {Object} payload.userId The id of the current user
 */
const parseContact = ({ rawContactObject, type, userId }) => {
  let status = ES_STATUS.pending;
  if (rawContactObject.opted_out_users && rawContactObject.opted_out_users.includes(userId)) {
    status = ES_STATUS.denied;
  } else if (rawContactObject.opted_in_users && rawContactObject.opted_in_users.includes(userId)) {
    status = ES_STATUS.approved;
  }
  return {
    [COMMUNICATION_TYPE.EMAIL]: {
      id: rawContactObject.id,
      first_name: rawContactObject.first_name || '',
      last_name: rawContactObject.last_name || '',
      email: rawContactObject.email || rawContactObject.email_primary || '',
      mobile_phone: rawContactObject.mobile_phone || rawContactObject.phone_mobile || '',
      opt_in_status: status,
    },
    [COMMUNICATION_TYPE.TEXT]: {
      id: rawContactObject.id,
      first_name: rawContactObject.first_name || '',
      last_name: rawContactObject.last_name || '',
      mobile_phone: rawContactObject.mobile_phone || rawContactObject.phone_mobile || '',
      opt_in_status: status,
      email: rawContactObject.email || rawContactObject.email_primary || '',
    },
  }[type]
};

const initialState = {
  recipientsInProgress: false,
  recipientsError: false,
  recipientsErrorMessage: '',
  allContacts: [],
  searchString: '',
  toSelected: [],
  ccSelected: [],
  bccSelected: [],
};

export default {
  namespaced: true,
  state: initialState,
  getters: {
    // Checks whether there is data in the recipients
    isRecipientsEmpty: state => {
      return (
        !state.toSelected.length
        && !state.ccSelected.length
        && !state.bccSelected.length
      );
    },
    allRecipients(state) {
      return [...state.toSelected, ...state.ccSelected, ...state.bccSelected];
    },
    ccAndBCCRecipients(state) {
      return [...state.ccSelected, ...state.bccSelected];
    },
  },
  mutations: {
    // Re-initializes the state
    resetState(state) {
      state = initialState;
    },
    clearErrors(state) {
      state.recipientsError = false;
      state.recipientsErrorMessage = '';
    },
    recipientsInProgress(state, isRecipientsInProgress) {
      state.recipientsInProgress = isRecipientsInProgress;
      state.recipientsError = false;
    },
    recipientsError(state, error) {
      state.recipientsErrorMessage = `Templates: ${error.code} ${error.message}`;
      state.recipientsInProgress = false;
      state.recipientsError = true;
    },
    setAllContacts(state, contacts) {
      state.allContacts = contacts;
      state.recipientsInProgress = false;
    },
    setSearchString(state, searchString) {
      state.searchString = searchString;
    },
    setToSelected(state, contacts) {
      state.toSelected = contacts;
    },
    setCcSelected(state, contacts) {
      state.ccSelected = contacts;
    },
    setBccSelected(state, contacts) {
      state.bccSelected = contacts;
    },
  },
  actions: {
    resetState({ commit }) {
      commit('resetState');
    },
    clearErrors({ commit }) {
      commit('clearErrors');
    },
    // Sets the intended TO recipients
    setToSelected({ commit }, contacts) {
      commit('setToSelected', contacts);
    },
    // Sets the intended CC recipients
    setCcSelected({ commit }, contacts) {
      commit('setCcSelected', contacts);
    },
    // Sets the intended BCC recipients
    setBccSelected({ commit }, contacts) {
      commit('setBccSelected', contacts);
    },
    /*
     * setSearchString sets the desired search string
     * and gathes the contacts and users accordingly.
     */
    setSearchString({ commit, dispatch }, { searchValue, type }) {
      commit('setSearchString', searchValue);
      if (searchValue && searchValue.length > 2) {
        dispatch('getContactsAndUsers', type);
      } else {
        commit('setAllContacts', []);
      }
    },
    /*
     * Get contacts is a function to query the `users` and `contacts` service
     * for possible matches in `first_name`, `last_name`, and `emails`
     */
    async getContactsAndUsers({ state, commit, rootState }, type) {
      commit('recipientsInProgress', true);

      // Construct the basic query
      const $select = {
        [COMMUNICATION_TYPE.EMAIL]: ['id', 'first_name', 'last_name', 'email'],
        // Backend errors out when including 'mobile_phone' in $select
        // TODO NA: Uncomment once backend bug is resolved
        // [COMMUNICATION_TYPE.TEXT]: ['id', 'first_name', 'last_name', 'mobile_phone'],
      }[type];

      const query = {
        $limit: 50,
        $select,
      };
      // If there is a search string present, construct additional query
      // to filter by first_name, last_name, and email
      if (state.searchString) {
        query.$or = [
          { first_name: { $like: `%${state.searchString}%` } },
          { last_name: { $like: `%${state.searchString}%` } },
        ];
        if (type === COMMUNICATION_TYPE.EMAIL) {
          query.$or.push({ email: { $like: `%${state.searchString}%` } });
        }
      }
      // First object is initialized to be the header so that the users are clear
      // by the given input
      let response = [{ header: 'Select a contact' }];
      try {
        await Promise.all([
          feathers.service('contacts').find(
            { query, headers: ESSearchHeader },
          ),
          // TODO ak: investigate why users were being
          // queried and how this functionality can be
          // restored - taken out to address issue #301
          // feathers.service('users').find({ query }),
        ]).then(values => {
          values.forEach(value => {
            // If there is data returned, append them to the resulting array
            if (value.data && value.data.length) {
              const parsedContactsOrUsers = [];
              for (let i = 0; i < value.data.length; i++) {
                // Iterates through the data and appends the necessary variables
                const parsedContactOrUser = parseContact({ rawContactObject: value.data[i], type, userId: rootState.userInformation.user.id });
                parsedContactsOrUsers.push(parsedContactOrUser);
              }
              response = response.concat(parsedContactsOrUsers);
            }
          });
        });
      } catch (error) {
        return commit('recipientsError', error);
      }
      // Persist the last available data instead of showing nothing to the users
      // There is at least one default object, which is the `header`.
      if (response.length > 1) commit('setAllContacts', response);
      commit('recipientsInProgress', false);
    },
  },
};
